import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, errorMessage } from '../api/client';
import { pluralize } from '../lib/text';
import { Dialog } from './Dialog';
import { InUseList } from './States';

interface RetireItem {
  id: number;
  name: string;
  to: string;
}

interface Refusal {
  item: RetireItem;
  error: unknown;
}

export function RetireDialog({
  noun,
  items,
  description,
  inUseLead,
  retire,
  onClose,
  onFinished,
}: {
  noun: 'slide' | 'group';
  items: RetireItem[];
  description: string;
  inUseLead: string;
  retire: (item: RetireItem) => Promise<unknown>;
  onClose: () => void;
  onFinished: (retired: number) => void;
}) {
  const [targets] = useState(items);
  const [progress, setProgress] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<{ retired: number; refused: Refusal[] } | null>(null);
  const nouns = `${noun}s`;
  const Noun = `${noun[0]?.toUpperCase()}${nouns.slice(1)}`;

  const run = async () => {
    const refused: Refusal[] = [];
    let retired = 0;
    for (const [index, item] of targets.entries()) {
      setProgress(index);
      try {
        await retire(item);
        retired += 1;
      } catch (err) {
        refused.push({ item, error: err });
      }
    }
    setProgress(null);
    onFinished(retired);
    if (refused.length === 0) onClose();
    else setOutcome({ retired, refused });
  };

  const busy = progress !== null;

  return (
    <Dialog
      title={
        outcome
          ? `${outcome.retired} of ${pluralize(targets.length, noun)} retired`
          : `Retire ${pluralize(targets.length, noun)}?`
      }
      onClose={onClose}
      closeDisabled={busy}
      footer={
        outcome ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void run()} disabled={busy}>
              {busy ? `Retiring ${(progress ?? 0) + 1} of ${targets.length}…` : 'Retire'}
            </button>
          </>
        )
      }
    >
      {outcome ? (
        <>
          <p>
            {outcome.retired > 0 ? `${pluralize(outcome.retired, noun)} retired. ` : ''}
            {outcome.refused.length === 1
              ? `This ${noun} was not retired:`
              : `These ${outcome.refused.length} ${nouns} were not retired:`}
          </p>
          <ul className="list-plain" aria-label={`${Noun} not retired`} style={{ maxHeight: 320, overflowY: 'auto' }}>
            {outcome.refused.map(({ item, error }) => {
              const inUse = error instanceof ApiError ? error.inUse : null;
              return (
                <li key={item.id} className="alert alert-error small">
                  <div className="stack" style={{ gap: 4 }}>
                    <Link to={item.to} onClick={onClose}>
                      <strong>{item.name}</strong>
                    </Link>
                    {inUse ? (
                      <div>
                        {inUseLead}
                        <InUseList details={inUse} />
                      </div>
                    ) : (
                      <div>{errorMessage(error)}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <>
          <p className="small muted">{description}</p>
          <ul className="small" style={{ margin: 0, paddingLeft: 20, maxHeight: 240, overflowY: 'auto' }}>
            {targets.map((item) => (
              <li key={item.id}>{item.name}</li>
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}
